import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { OrgNameAndIndustryBoxFields } from 'src/components/OrgNameAndIndustryBox/OrgNameAndIndustryBox'
import TextDropdown, {
  TextOption,
} from 'src/components/TextDropdown/TextDropdown'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { PatchOrganizationRequest } from 'src/requests/patchOrganization.request'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

interface OrgNameAndIndustryFormProps {
  industryOptions: TextOption[]
  putOrganization: (data: Partial<PatchOrganizationRequest>) => void
  puttingOrganization: boolean
  onCancel: () => void
  orgName: string
  orgIndustry: string
  topLevelError?: string
}

const OrgNameAndIndustryForm: React.FC<OrgNameAndIndustryFormProps> = (
  props,
): React.ReactElement => {
  const { analytics } = useAnalyticsContext()

  const manageOrganizationFieldsValidation =
    (): FormFieldsValidation<OrgNameAndIndustryBoxFields> => ({
      name: [stringIsEmpty()],
      industry: [stringIsEmpty()],
    })

  const {
    fields: organizationFlatFields,
    setFields: setOrganizationFlatFields,
    fieldsErrors: organizationFlatFieldsErrors,
    onFieldChange: organizationFlatFieldChange,
    setErrorsAndReturnDidPassValidation: organizationFlatFieldsSetErrors,
  } = useFormFields<OrgNameAndIndustryBoxFields>(
    {},
    manageOrganizationFieldsValidation,
    undefined,
    undefined,
    {
      name: 2,
      industry: 2,
    },
  )

  useEffect((): void => {
    setOrganizationFlatFields({
      name: props.orgName,
      industry: props.orgIndustry,
    })
  }, [props.orgName, props.orgIndustry])

  const locale = useSelector(selectNullSafeLocaleFromState)

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    const organizationFlatFieldsPassedValidation =
      organizationFlatFieldsSetErrors()
    if (organizationFlatFieldsPassedValidation) {
      props.putOrganization({
        name: organizationFlatFields['name'],
        industry: organizationFlatFields['industry'],
      })
    }
  }

  const [orgName, setOrgName] = useState<boolean>(false)
  const [industryName, setIndustryName] = useState<boolean>(false)

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <form
        onSubmit={handleOnSubmit}
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <Fragment>
            <TextInputField
              errors={
                props.topLevelError !== undefined
                  ? [props.topLevelError]
                  : organizationFlatFieldsErrors['name']
              }
              design={TextInputFieldDesigns.New}
              baseValue={props.orgName}
              fieldLabel={<Trns source={'Organization Name'} />}
              value={organizationFlatFields['name']}
              onChange={(val: string): void => {
                organizationFlatFieldChange('name', val)
                setOrgName(true)
              }}
              placeholder={translateSingleString(locale, 'Enter your org name')}
              css={css`
                &:nth-child(n + 2) {
                  margin-top: 24px;
                }
              `}
            />
            <TextDropdown
              fieldLabel={<Trns source={'Industry'} />}
              optionGroups={[
                {
                  label: <Trns source={'Existing Options'} />,
                  options: props.industryOptions,
                },
              ]}
              isMulti={false}
              selectedValues={
                organizationFlatFields['industry'] !== undefined
                  ? new Set([organizationFlatFields['industry']])
                  : new Set<string>()
              }
              chooseOption={(val: string): void => {
                organizationFlatFieldChange('industry', val)
                setIndustryName(true)
              }}
              placeholder={translateSingleString(
                locale,
                'Select or type in your industry',
              )}
              errors={organizationFlatFieldsErrors['industry']}
              validateNewOption={(val: string): boolean => val.length > 0}
              newOptionLabel={<Trns source={'New Label'} />}
              isCreatable
              css={css`
                &:nth-child(n + 2) {
                  margin-top: 24px;
                }
              `}
            />
          </Fragment>
        </div>
        <div
          css={css`
            margin-top: 40px;
            display: flex;
            align-items: center;
          `}
        >
          <Button
            onClick={(): void => {
              analytics?.track('company info', {
                update_name: orgName,
                update_industry: industryName,
              })
            }}
            type={ButtonTypes.submit}
            design={ButtonDesigns.designCta}
            text={<Trns source={'Save Updates'} />}
            big
            loading={props.puttingOrganization}
            css={css`
              align-self: flex-start;
            `}
          />
          <HoverableLink
            css={css`
              margin-left: 20px;
            `}
            onClick={props.onCancel}
            ariaLabelSource={'Cancel'}
          >
            <Trns source={'Cancel'} />
          </HoverableLink>
        </div>
      </form>
    </div>
  )
}

export default OrgNameAndIndustryForm
